import request from '@/utils/request';

/**
 * 作品管理相关 API
 */

/**
 * 分页查询作品列表
 * @param {Object} params - 查询参数
 * @param {number} params.pageNum - 页码
 * @param {number} params.pageSize - 每页条数
 * @param {string} [params.workName] - 作品名称（模糊搜索）
 * @param {number} [params.folderId] - 所属文件夹ID
 * @param {number} [params.archived] - 是否归档筛选：0-未归档 1-已归档
 * @param {Array} [params.orderByList] - 排序参数列表
 * @returns {Promise<Object>} 作品列表分页数据
 */
export function getWorkList(params) {
  const requestData = {
    pageNum: params.pageNum || 1,
    pageSize: params.pageSize || 10,
    workName: params.workName || '',
    folderId: params.folderId !== undefined && params.folderId !== null ? params.folderId : null,
    orderByList: params.orderByList || []
  };
  
  // 只有当 archived 有明确值时才添加该字段
  if (params.archived !== undefined && params.archived !== null) {
    requestData.archived = params.archived;
  }
  
  return request({
    url: '/user/work/page',
    method: 'post',
    data: requestData
  });
}

/**
 * 创建作品
 * @param {Object} data - 作品数据
 * @param {string} data.workName - 作品名称
 * @param {number} [data.coverImageId] - 封面图片ID
 * @param {string} [data.description] - 作品简介
 * @param {number} [data.folderId] - 所属文件夹ID
 * @param {number} [data.top] - 是否置顶 0-不置顶 1-置顶
 * @returns {Promise<Object>} 创建结果
 */
export function createWork(data) {
  return request({
    url: '/user/work/create',
    method: 'post',
    data: {
      workName: data.workName,
      coverImageId: data.coverImageId || null,
      description: data.description || '',
      folderId: data.folderId || null,
      top: data.top || 0
    }
  });
}

/**
 * 编辑作品
 * @param {Object} data - 作品数据
 * @param {number} data.id - 作品ID
 * @param {string} data.workName - 作品名称
 * @param {number} [data.coverImageId] - 封面图片ID
 * @param {string} [data.description] - 作品简介
 * @param {number} [data.folderId] - 所属文件夹ID（为空表示未归档）
 * @param {number} [data.top] - 是否置顶 0-不置顶 1-置顶
 * @returns {Promise<Object>} 更新结果
 */
export function updateWork(data) {
  return request({
    url: '/user/work/update',
    method: 'post',
    data: {
      id: data.id,
      workName: data.workName,
      coverImageId: data.coverImageId || null,
      description: data.description || '',
      folderId: data.folderId !== undefined && data.folderId !== null ? data.folderId : null,
      top: data.top !== undefined ? data.top : 0
    }
  });
}

/**
 * 删除作品
 * @param {number} id - 作品ID
 * @returns {Promise<Object>}
 */
export function deleteWork(id) {
  return request({
    url: '/user/work/delete',
    method: 'post',
    data: { id }
  });
}

/**
 * 上传作品封面
 * @param {number} workId - 作品ID
 * @param {File} file - 封面图片文件
 * @returns {Promise<Object>} 上传结果，返回更新后的作品信息
 */
export function uploadWorkCover(workId, file) {
  const formData = new FormData();
  formData.append('file', file);
  return request({
    url: '/user/work/uploadCover',
    method: 'post',
    params: { id: workId },
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
}

/**
 * 置顶作品
 * @param {number} id - 作品ID
 * @returns {Promise<Object>}
 */
export function pinWork(id) {
  return request({
    url: '/user/work/pin',
    method: 'post',
    data: { id }
  });
}

/**
 * 取消置顶作品
 * @param {number} id - 作品ID
 * @returns {Promise<Object>}
 */
export function unpinWork(id) {
  return request({
    url: '/user/work/unpin',
    method: 'post',
    data: { id }
  });
}

/**
 * 获取文件夹列表
 * @returns {Promise<Object>}
 */
export function getFolderList() {
  return request({
    url: '/user/work/folder/list',
    method: 'post'
  });
}

/**
 * 创建文件夹
 * @param {Object} data - 文件夹数据
 * @param {string} data.folderName - 文件夹名称
 * @returns {Promise<Object>}
 */
export function createFolder(data) {
  return request({
    url: '/user/work/folder/create',
    method: 'post',
    data: {
      folderName: data.folderName
    }
  });
}

/**
 * 更新文件夹
 * @param {Object} data - 文件夹数据
 * @param {number} data.id - 文件夹ID
 * @param {string} data.folderName - 文件夹名称
 * @returns {Promise<Object>}
 */
export function updateFolder(data) {
  return request({
    url: '/user/work/folder/update',
    method: 'post',
    data: {
      id: data.id,
      folderName: data.folderName
    }
  });
}

/**
 * 删除文件夹
 * @param {number} id - 文件夹ID
 * @returns {Promise<Object>}
 */
export function deleteFolder(id) {
  return request({
    url: '/user/work/folder/delete',
    method: 'post',
    data: { id }
  });
}

/**
 * 获取作品详情
 * @param {number} id - 作品ID
 * @returns {Promise<Object>}
 */
export function getWorkDetail(id) {
  return request({
    url: '/user/work/detail',
    method: 'post',
    data: { id }
  });
}

/**
 * 获取章节列表
 * @param {Object} params - 查询参数
 * @param {number} params.workId - 作品ID
 * @returns {Promise<Object>}
 */
export function getChapterList(params) {
  return request({
    url: '/user/work/chapter/page',
    method: 'post',
    data: {
      workId: params.workId,
      volumeId: params.volumeId || null,
      pageNum: params.pageNum || 1,
      pageSize: params.pageSize || 50,
      orderByList: params.orderByList || []
    }
  });
}

/**
 * 获取章节简单列表（最近N章）
 * @param {Object} params - 查询参数
 * @param {number} params.workId - 作品ID
 * @param {number} params.limit - 限制数量（-1表示全部）
 * @param {number} [params.excludeChapterId] - 排除章节ID
 * @returns {Promise<Object>}
 */
export function getChapterSimpleList(params) {
  return request({
    url: '/user/work/chapter/simpleList',
    method: 'post',
    data: {
      workId: params.workId,
      limit: params.limit !== undefined ? params.limit : 5,
      excludeChapterId: params.excludeChapterId || null
    }
  });
}

/**
 * 获取章节详情
 * @param {number} id - 章节ID
 * @returns {Promise<Object>}
 */
export function getChapterDetail(id) {
  return request({
    url: '/user/work/chapter/detail',
    method: 'post',
    data: { id }
  });
}

/**
 * 查看章节摘要
 * @param {number} id - 章节ID
 * @returns {Promise<Object>}
 */
export function getChapterDetailSummary(id) {
  return request({
    url: '/user/work/chapter/detailSummary',
    method: 'post',
    data: { id }
  });
}

/**
 * 创建章节
 * @param {Object} data - 章节数据
 * @param {number} data.workId - 作品ID
 * @param {string} data.chapterTitle - 章节标题
 * @param {string} data.content - 章节内容
 * @returns {Promise<Object>}
 */
export function createChapter(data) {
  return request({
    url: '/user/work/chapter/create',
    method: 'post',
    data: {
      workId: data.workId,
      chapterTitle: data.chapterTitle,
      content: data.content || ''
    }
  });
}

/**
 * 插入章节（向前/向后插入）
 * @param {Object} data - 插入参数
 * @param {number} data.workId - 作品ID（必填）
 * @param {number} data.refChapterId - 参考章节ID（必填）
 * @param {number} data.direction - 插入方向 1-向前插入 2-向后插入（必填）
 * @param {string} [data.chapterName] - 章节名称（为空时自动生成"第x章"）
 * @param {number} [data.volumeId] - 所属分卷ID（为空时跟随参考章节的分卷）
 * @returns {Promise<Object>}
 */
export function insertChapter(data) {
  return request({
    url: '/user/work/chapter/insert',
    method: 'post',
    data: {
      workId: data.workId,
      refChapterId: data.refChapterId,
      direction: data.direction,
      chapterName: data.chapterName || null,
      volumeId: data.volumeId || null
    }
  });
}

/**
 * 更新章节内容
 * @param {Object} data - 章节数据
 * @param {number} data.id - 章节ID
 * @param {string} data.content - 章节内容
 * @param {number} data.wordCount - 字数
 * @returns {Promise<Object>}
 */
export function updateChapterContent(data) {
  return request({
    url: '/user/work/chapter/updateContent',
    method: 'post',
    data: {
      id: data.id,
      content: data.content || '',
      wordCount: data.wordCount || 0
    }
  });
}

/**
 * 删除章节
 * @param {number} id - 章节ID
 * @returns {Promise<Object>}
 */
export function deleteChapter(id) {
  return request({
    url: '/user/work/chapter/delete',
    method: 'post',
    data: { id }
  });
}

/**
 * AI写作
 * @param {Object} data - AI写作参数
 * @param {string} data.action - 操作类型：write/continue/polish/expand/shorten
 * @param {string} data.text - 输入文本
 * @param {number} data.modelId - 模型ID
 * @param {number} data.temperature - 温度
 * @param {number} data.maxTokens - 最大token数
 * @returns {Promise<Object>}
 */
export function aiWrite(data) {
  return request({
    url: '/ai/write',
    method: 'post',
    data: {
      action: data.action,
      text: data.text,
      modelId: data.modelId,
      temperature: data.temperature,
      maxTokens: data.maxTokens
    }
  });
}

/**
 * 重命名章节
 * @param {Object} data - 章节数据
 * @param {number} data.id - 章节ID
 * @param {string} data.chapterName - 新章节名称
 * @returns {Promise<Object>}
 */
export function renameChapter(data) {
  return request({
    url: '/user/work/chapter/rename',
    method: 'post',
    data: {
      id: data.id,
      chapterName: data.chapterName
    }
  });
}

/**
 * 编辑章节摘要
 * @param {Object} data - 章节数据
 * @param {number} data.id - 章节ID
 * @param {string} [data.summary] - 章节概要（最多2000字符）
 * @returns {Promise<Object>}
 */
export function updateChapterSummary(data) {
  return request({
    url: '/user/work/chapter/updateSummary',
    method: 'post',
    data: {
      id: data.id,
      summary: data.summary || ''
    }
  });
}

/**
 * 查询分卷列表
 * @param {Object} params - 查询参数
 * @param {number} params.workId - 作品ID
 * @returns {Promise<Object>}
 */
export function getVolumeList(params) {
  return request({
    url: '/user/work/volume/list',
    method: 'post',
    data: {
      workId: params.workId
    }
  });
}

/**
 * 执行AI写作（流式输出 - SSE）
 * 后端返回 Content-Type: text/event-stream，数据格式为 SSE 流
 * @param {Object} data - 请求参数
 * @param {number} data.aiModelId - AI模型ID
 * @param {number} data.writeType - 写作类型 1-AI写作 2-创意中心
 * @param {number} [data.writeKind] - 写作细分类型 1-导语 2-大纲 3-简介 4-细纲 5-续写 6-润色 7-扩写 8-缩写 9-AI写作 10-审稿 11-纠错 12-命名 13-追问
 * @param {number} [data.creativeId] - 创意ID（创意中心使用）
 * @param {number} [data.promptId] - 系统提示词ID
 * @param {string} [data.customPrompt] - 用户自定义提示词
 * @param {string} [data.supplementData] - 补充信息（自定义模式下用户输入的详细要求）
 * @param {string} [data.followUpQuestion] - 追问问题（writeKind=13时使用）
 * @param {Object} [data.data] - 提示词动态字段数据（快速模式下表单值）
 * @param {Array<{chapterId:number,type:number}>} [data.chapters] - 知识库-章节列表 type:1-正文 2-概要
 * @param {number[]} [data.memoIds] - 知识库-备忘录ID列表
 * @param {number} [data.workId] - 作品ID
 * @param {number} [data.currentChapterId] - 当前章节ID（细纲生成等场景使用）
 * @param {number} [data.selectContent] - 选中的文本内容（续写/润色等场景使用）
 * @param {number} [data.lead] - 是否关联导语 0-否 1-是
 * @param {number} [data.outline] - 是否关联大纲 0-否 1-是
 * @param {number} [data.intro] - 是否关联简介 0-否 1-是
 * @param {number} [data.summary] - 是否关联章节概要 0-否 1-是
 * @param {number} [data.temperature] - 模型温度参数（联想能力）
 * @param {number} [data.thinkTokens] - 思考预算（思想深度）
 * @param {number} [data.maxTokens] - 最大生成token数
 * @param {Function} onMessage - 接收到消息的回调函数
 *   参数1: data - 解析后的 SSE 数据对象（如 {content: '文本片段'}）
 *   参数2: metadata - SSE 元数据 { event, id, raw }
 * @param {Function} onComplete - 请求完成的回调函数
 * @param {Function} onError - 请求错误的回调函数
 * @returns {Promise<{abort: Function}>} - 返回包含 abort 方法的对象，用于取消请求
 */
export function executeAiWriteStream(data, onMessage, onComplete, onError) {
  // 动态导入 SSE 流式工具（使用 fetch 支持 POST 请求）
  return import('@/utils/sseStream').then(({ createSSEStreamPost }) => {
    return createSSEStreamPost({
      url: '/user/aiWrite/execute',
      method: 'POST',
      data,
      onMessage,
      onComplete,
      onError
    });
  });
}

/**
 * 获取作品导语
 * @param {number} workId - 作品ID
 * @returns {Promise<Object>}
 */
export function getWorkLead(workId) {
  return request({
    url: '/user/work/lead/get',
    method: 'post',
    data: {
      id: workId
    }
  });
}

/**
 * 获取作品大纲
 * @param {number} workId - 作品ID
 * @returns {Promise<Object>}
 */
export function getWorkOutline(workId) {
  return request({
    url: '/user/work/outline/get',
    method: 'post',
    data: {
      id: workId
    }
  });
}

/**
 * 获取作品简介
 * @param {number} workId - 作品ID
 * @returns {Promise<Object>}
 */
export function getWorkIntro(workId) {
  return request({
    url: '/user/work/intro/get',
    method: 'post',
    data: {
      id: workId
    }
  });
}

/**
 * AI生成大纲
 * @param {Object} data - 参数
 * @param {number} data.workId - 作品ID
 * @param {string} data.type - 生成类型
 * @returns {Promise<Object>}
 */
export function generateOutline(data) {
  return request({
    url: '/user/work/outline/generate',
    method: 'post',
    data: {
      workId: data.workId,
      type: data.type
    }
  });
}

/**
 * 保存作品导语
 * @param {Object} data - 参数
 * @param {number} data.id - 作品ID
 * @param {string} data.intro - 导语内容
 * @returns {Promise<Object>}
 */
export function saveWorkLead(data) {
  return request({
    url: '/user/work/lead/save',
    method: 'post',
    data: {
      id: data.id,
      lead: data.lead
    }
  });
}

/**
 * 保存作品大纲
 * @param {Object} data - 参数
 * @param {number} data.id - 作品ID
 * @param {string} data.outline - 大纲内容
 * @returns {Promise<Object>}
 */
export function saveWorkOutline(data) {
  return request({
    url: '/user/work/outline/save',
    method: 'post',
    data: {
      id: data.id,
      outline: data.outline
    }
  });
}

/**
 * 保存作品简介
 * @param {Object} data - 参数
 * @param {number} data.id - 作品ID
 * @param {string} data.intro - 简介内容
 * @returns {Promise<Object>}
 */
export function saveWorkIntro(data) {
  return request({
    url: '/user/work/intro/save',
    method: 'post',
    data: {
      id: data.id,
      intro: data.intro
    }
  });
}

/**
 * 获取作品额外信息统计（导语/大纲/简介字数）
 * @param {number} workId - 作品ID
 * @returns {Promise<Object>}
 */
export function getWorkExtraCount(workId, chapterId) {
  return request({
    url: '/user/work/extra-count',
    method: 'post',
    data: { id: workId, chapterId: chapterId }
  });
}

/**
 * AI写作追问（流式输出 - SSE）
 * 基于已有会话进行追问，后端返回 Content-Type: text/event-stream
 * @param {Object} data - 请求参数
 * @param {number} data.aiModelId - AI模型ID（必填）
 * @param {string} data.followUpQuestion - 追问的用户提问（必填）
 * @param {string} [data.conversationId] - 会话记录ID，与 selectContent 二选一
 * @param {string} [data.selectContent] - 选中的信息/前文内容，与 conversationId 二选一
 * @param {number} [data.workId] - 作品ID
 * @param {number[]} [data.memoIds] - 知识库-备忘录ID列表
 * @param {number} [data.temperature] - 模型温度参数（联想能力）
 * @param {number} [data.thinkTokens] - 思考预算（思想深度）
 * @param {number} [data.maxTokens] - 最大生成token数
 * @param {Function} onMessage - 接收到消息的回调函数
 *   参数1: data - 解析后的 SSE 数据对象，常见格式：
 *     - { content: "文本片段" } - 正常内容块
 *     - { content: "...", type: "reasoning" } - 推理内容
 *     - { type: "progress", percent: 50, stage: "thinking" } - 进度信息
 *     - { type: "metadata", totalTokens: 1234 } - 元数据
 *   参数2: metadata - SSE 元数据 { event, id, raw }
 * @param {Function} onComplete - 请求完成的回调函数，参数为 done 事件数据（如有）
 * @param {Function} onError - 请求错误的回调函数，参数为 Error 对象
 * @returns {Promise<{abort: Function}>} - 返回包含 abort 方法的对象，用于取消请求
 */
export function followUpStream(data, onMessage, onComplete, onError) {
  // 动态导入 SSE 流式工具（使用 fetch 支持 POST 请求）
  return import('@/utils/sseStream').then(({ createSSEStreamPost }) => {
    return createSSEStreamPost({
      url: '/user/aiWrite/question',
      method: 'POST',
      data,
      onMessage,
      onComplete,
      onError
    });
  });
}

/**
 * AI写作重新生成（流式输出 - SSE）
 * 基于已有记录重新生成内容，后端返回 Content-Type: text/event-stream
 * @param {Object} data - 请求参数
 * @param {string} data.conversationId - 会话记录ID（必填）
 * @param {string} data.requestId - 请求ID（必填，新生成的请求ID）
 * @param {Function} onMessage - 接收到消息的回调函数
 *   参数1: data - 解析后的 SSE 数据对象，常见格式：
 *     - { content: "文本片段" } - 正常内容块
 *     - { content: "...", type: "reasoning" } - 推理内容
 *     - { type: "progress", percent: 50, stage: "thinking" } - 进度信息
 *     - { type: "metadata", totalTokens: 1234 } - 元数据
 *   参数2: metadata - SSE 元数据 { event, id, raw }
 * @param {Function} onComplete - 请求完成的回调函数，参数为 done 事件数据（如有）
 * @param {Function} onError - 请求错误的回调函数，参数为 Error 对象
 * @returns {Promise<{abort: Function}>} - 返回包含 abort 方法的对象，用于取消请求
 */
export function regenerateStream(data, onMessage, onComplete, onError) {
  // 动态导入 SSE 流式工具（使用 fetch 支持 POST 请求）
  return import('@/utils/sseStream').then(({ createSSEStreamPost }) => {
    return createSSEStreamPost({
      url: '/user/aiWrite/regenerate',
      method: 'POST',
      data,
      onMessage,
      onComplete,
      onError
    });
  });
}
